import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Outlet, useLocation } from "react-router";
import { APPLICATION_STEPS } from "constants/index";
import { useAuth, useLinks, useMissionRoleApplicant } from "hooks";
import { Grid, NarrowContainer } from "components/Containers";
import Header from "components/Header";
import Impersonating from "components/Impersonating";
import { GlobalApplicantProgress, ApplicantRoleUnavailable, ApplicantContactUs } from "components/Missions/Applicants";
import { CandidateProgress, CandidateRoleDetails, CandidateStatus } from "components/Candidate";
import Card from "components/Card";
import { Spinner } from "react-bootstrap";
import { PublicFooter } from "components/Public";
import { ProfileProgress } from "components/Profile";
import { isApplicationStepOptional } from "utils";

/**
 * Home
 */
const Home = () => {
  const location = useLocation();
  const { profileId, originatedMissionRoleApplicant: isRoleApplicant } = useAuth();
  const { data: application, loading: loadingApplication } = useMissionRoleApplicant({ id: isRoleApplicant?.id });
  const [rateEditing, setRateEditing] = useState(false);
  const links = useLinks();
  const [questionnaireEditing, setQuestionnaireEditing] = useState(false);
  const [requirementsEditing, setRequirementsEditing] = useState(false);
  const [hideFooter, setHideFooter] = useState(false);
  const isProfilePage = location.pathname === links.candidate_profile;
  const PAGES = [
    ...(isRoleApplicant ? ROLE_APPLICATION_PAGE(links.candidate_home) : HOME_PAGE(links.candidate_home)),
    ...PROFILE_PAGE(links.candidate_profile),
  ];
  const hasCodeChallengeRequirement = application?.applicationChecklist?.some(
    (s) => s.name === APPLICATION_STEPS.code_challenge
  );
  const hasVideoInterviewRequirement = application?.applicationChecklist?.some(
    (s) => s.name === APPLICATION_STEPS.video_interview
  );
  const isVideoInterviewOptional = isApplicationStepOptional(
    application?.missionRole?.applicationSteps,
    APPLICATION_STEPS.video_interview
  );
  const isProfileOptional = isApplicationStepOptional(
    application?.missionRole?.applicationSteps,
    APPLICATION_STEPS.profile_completion
  );

  return (
    <>
      <Header pages={PAGES} />
      <ApplicantRoleUnavailable application={application} />
      <NarrowContainer>
        <Grid topMargin bottomMargin>
          <Grid.col start={1} end={9}>
            {loadingApplication && <Spinner />}
            {!loadingApplication && (
              <Outlet
                context={{
                  application,
                  loadingApplication,
                  profileId,
                  hasCodeChallengeRequirement,
                  hasVideoInterviewRequirement,
                  rateEditing,
                  setRateEditing,
                  questionnaireEditing,
                  setQuestionnaireEditing,
                  requirementsEditing,
                  setRequirementsEditing,
                  setHideFooter,
                  isVideoInterviewOptional,
                  isProfileOptional,
                }}
              />
            )}
          </Grid.col>
          <Grid.col start={9} end={13}>
            <Grid cols={1}>
              <Grid.col start={1} end={1}>
                <Card>
                  {application ? (
                    <GlobalApplicantProgress
                      path={location.pathname}
                      missionRoleApplicant={application}
                      rateEditing={rateEditing}
                      setRateEditing={setRateEditing}
                      questionnaireEditing={questionnaireEditing}
                      setQuestionnaireEditing={setQuestionnaireEditing}
                      setRequirementsEditing={setRequirementsEditing}
                    />
                  ) : (
                    <CandidateProgress profileId={profileId} />
                  )}
                </Card>
              </Grid.col>
              {!application && isProfilePage && (
                <Grid.col>
                  <Card>
                    <ProfileProgress id={profileId} />
                  </Card>
                </Grid.col>
              )}
              <Grid.col>
                <CandidateStatus />
              </Grid.col>
              {application && (
                <Grid.col>
                  <Card>
                    <CandidateRoleDetails role={application?.missionRole} mission={application?.missionRole?.mission} />
                  </Card>
                </Grid.col>
              )}
              <Grid.col>
                <ApplicantContactUs />
              </Grid.col>
            </Grid>
          </Grid.col>
        </Grid>
        <Impersonating offset={0} />
      </NarrowContainer>
      {!hideFooter && <PublicFooter />}
    </>
  );
};

const ROLE_APPLICATION_PAGE = (candidateHomeLink) => [
  {
    to: candidateHomeLink,
    label: <FormattedMessage id="Nav.Application" />,
  },
];

const HOME_PAGE = (candidateHomeLink) => [
  {
    to: candidateHomeLink,
    label: <FormattedMessage id="Nav.Candidate.Home" />,
  },
];

const PROFILE_PAGE = (candidateProfileLink) => [
  {
    to: candidateProfileLink,
    label: <FormattedMessage id="Nav.Candidate.Profile" />,
  },
];

export default Home;
